// Equipment Scopes editor — the office writes one scope of work per equipment
// category here, and every tech who opens a checklist for a piece of equipment
// in that category sees it. One scope for "Boiler" covers all five boilers in a
// building rather than being restated on each.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaTimes, FaChevronDown, FaCheckCircle } from 'react-icons/fa';
import { fetchEquipmentScopes, saveEquipmentScopes } from './api/equipmentScopes';

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

export default function MechEquipmentScopesModal({ open, onClose }) {
  const [categories, setCategories] = useState([]);
  // Categories still showing Hudson's built-in scope rather than one the
  // office wrote. The text is real either way, so without this the editor
  // would report every category as written and done.
  const [defaults, setDefaults] = useState([]);
  const [saved, setSaved] = useState({});
  const [drafts, setDrafts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setJustSaved(false);
    fetchEquipmentScopes({ withCategories: true })
      .then((data) => {
        if (cancelled) return;
        setCategories(data.categories || []);
        setDefaults(data.defaults || []);
        setSaved(data.scopes || {});
        setDrafts(data.scopes || {});
        setExpanded(null);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Only what actually changed goes to the server, so a save can't overwrite a
  // category someone else edited while this modal was open.
  const dirty = useMemo(() => {
    const changed = {};
    Object.keys(drafts).forEach((category) => {
      if ((drafts[category] || '') !== (saved[category] || '')) changed[category] = drafts[category] || '';
    });
    return changed;
  }, [drafts, saved]);
  const dirtyCount = Object.keys(dirty).length;

  const handleSave = useCallback(async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    setError(null);
    try {
      await saveEquipmentScopes(dirty);
      setSaved((prev) => ({ ...prev, ...dirty }));
      setDefaults((prev) => prev.filter((c) => !Object.prototype.hasOwnProperty.call(dirty, c)));
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [dirty, dirtyCount]);

  if (!open) return null;

  const isDefault = (c) => defaults.includes(c);
  const written = categories.filter((c) => (saved[c] || '').trim() && !isDefault(c)).length;
  const onDefault = categories.filter((c) => isDefault(c)).length;
  const empty = categories.length - written - onDefault;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '2.5rem 1rem', overflowY: 'auto', fontFamily: sharedFont,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid #E6E8EC' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1a2236' }}>Equipment Scopes</h2>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 3 }}>
              One scope of work per category. Techs see it while filling out a checklist for any
              equipment in that category.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280', padding: 4, marginLeft: 12 }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: '1rem 1.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
          {loading && <div style={{ padding: '2rem 0', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Loading scopes…</div>}

          {!loading && (
            <>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: '0.75rem' }}>
                {written} of {categories.length} categories have a scope written here
                {onDefault > 0 && `, ${onDefault} on the Hudson default`}
                {empty > 0 && `, ${empty} with nothing to show`}.
                {onDefault > 0 && ' Edit a default to make it yours.'}
              </div>

              {categories.map((category) => {
                const isOpen = expanded === category;
                const usingDefault = isDefault(category);
                const hasScope = !usingDefault && (saved[category] || '').trim().length > 0;
                const isDirty = Object.prototype.hasOwnProperty.call(dirty, category);
                return (
                  <div key={category} style={{ border: '1px solid #E6E8EC', borderRadius: 10, marginBottom: '0.5rem', overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : category)}
                      style={{
                        width: '100%', padding: '0.7rem 0.9rem', background: isOpen ? '#F7F9FC' : '#fff',
                        border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', fontFamily: sharedFont,
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#1a2236' }}>
                        {category}
                        {hasScope && <FaCheckCircle style={{ color: '#16a34a', fontSize: 11 }} />}
                        {isDirty && <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>unsaved</span>}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {usingDefault && !isDirty && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#1F3A68', background: '#EEF2F8', borderRadius: 999, padding: '2px 8px' }}>
                            Hudson default
                          </span>
                        )}
                        {!hasScope && !usingDefault && !isDirty && <span style={{ fontSize: 11.5, color: '#9ca3af' }}>Not set</span>}
                        <FaChevronDown style={{ color: '#6b7280', fontSize: 11, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                      </span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: '0 0.9rem 0.9rem' }}>
                        <textarea
                          value={drafts[category] || ''}
                          onChange={(e) => setDrafts((prev) => ({ ...prev, [category]: e.target.value }))}
                          placeholder={`Scope of work for ${category}…`}
                          rows={10}
                          /* Monospace and no spellcheck: these get written with
                             hanging indents and numbered steps, and the tech
                             sees them laid out exactly as typed here. */
                          spellCheck={false}
                          style={{
                            width: '100%', padding: '0.7rem', border: '1px solid #E6E8EC', borderRadius: 8,
                            fontSize: 13, lineHeight: 1.55, outline: 'none', resize: 'vertical',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                            whiteSpace: 'pre-wrap', tabSize: 2,
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.9rem 1.5rem', borderTop: '1px solid #E6E8EC', background: '#FAFBFC' }}>
          <div style={{ fontSize: 12.5, color: error ? '#b91c1c' : '#6b7280', flex: 1, minWidth: 0 }}>
            {error || (justSaved ? 'Saved.' : (dirtyCount > 0 ? `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}` : 'Indentation and line breaks are kept exactly as you type them.'))}
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={dirtyCount === 0 || saving}
            style={{
              fontSize: 13, fontWeight: 700, padding: '8px 18px', borderRadius: 9, border: 'none',
              cursor: dirtyCount === 0 || saving ? 'not-allowed' : 'pointer',
              background: dirtyCount === 0 || saving ? '#D1D5DB' : '#1F3A68', color: '#fff',
              fontFamily: sharedFont, whiteSpace: 'nowrap',
            }}
          >
            {saving ? 'Saving…' : 'Save scopes'}
          </button>
        </div>
      </div>
    </div>
  );
}
